import { useCallback, useLayoutEffect, useRef } from "react";

// Readability floor — content never shrinks below this. Paired with the
// authored choice-length cap, this keeps even a dense card legible: the cap
// bounds worst-case input so content still fits at/above this scale.
const MIN_SCALE = 0.7;
// Binary-search steps. 8 → final scale is within ~0.4% of the true fit.
const MAX_ITERATIONS = 8;

/**
 * Fit-to-height for swipe-deck quiz/poll cards.
 *
 * The player can't scroll (a vertical scroll would swallow the deck's
 * horizontal swipe gesture), so a long question + answers would otherwise clip
 * the lower choices or the Submit button with no way to reach them. This shrinks
 * — never grows — the content's font size via a binary-searched `--fit-scale`
 * CSS var until the card stops overflowing its fixed height.
 *
 * Attach `cardRef` to the card root (the fixed-height box) and `contentRef` to
 * the content column. The column's text must be expressed in `em` / a
 * `calc(... * var(--fit-scale, 1))` font-size so a smaller scale genuinely
 * reflows (real layout, not a transform — a transform wouldn't change the
 * measured height). At scale 1 the card is pixel-identical to before, so the
 * common short-answer case is completely untouched; this only engages on the
 * rare dense card.
 *
 * Re-fits before paint when `deps` change (question/choices/answered state and
 * the image band, which settles on photo load), on card resize (viewport /
 * orientation / panel dock), and after web fonts load (their metrics shift the
 * text). `enabled: false` (display-only preview tiles) leaves the card at
 * scale 1.
 */
export function useFitToHeight({ enabled = true, deps = [] }) {
  const cardRef = useRef(null);
  const contentRef = useRef(null);

  const fit = useCallback(() => {
    const card = cardRef.current;
    const content = contentRef.current;
    if (!card || !content) return;
    const apply = (s) => content.style.setProperty("--fit-scale", String(s));
    if (!enabled) {
      apply(1);
      return;
    }
    // Reading scrollHeight on the overflow-hidden card detects clipped content
    // directly, so the search accounts for the image band's height implicitly.
    const overflows = () => card.scrollHeight - card.clientHeight > 0.5;
    apply(1);
    if (!overflows()) return; // already fits → stay identical to before
    let lo = MIN_SCALE;
    let hi = 1;
    let best = MIN_SCALE;
    for (let i = 0; i < MAX_ITERATIONS; i++) {
      const mid = (lo + hi) / 2;
      apply(mid);
      if (overflows()) {
        hi = mid; // still too big — shrink further
      } else {
        best = mid; // fits — try to give back a little
        lo = mid;
      }
    }
    apply(best);
  }, [enabled]);

  // Fit before paint whenever the content inputs change (no big-then-shrink
  // flicker). Re-runs the single compute, never reacting to its own scale
  // writes (card.clientHeight is fixed by the parent), so there's no loop.
  useLayoutEffect(() => {
    fit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fit, ...deps]);

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!enabled || !card || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const ro = new ResizeObserver(() => fit());
    ro.observe(card);
    return () => ro.disconnect();
  }, [fit, enabled]);

  useLayoutEffect(() => {
    if (!enabled || typeof document === "undefined" || !document.fonts?.ready) {
      return undefined;
    }
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) fit();
    });
    return () => {
      cancelled = true;
    };
  }, [fit, enabled]);

  return { cardRef, contentRef };
}
